// Geo distance helpers never block transitions — status surfaces via banner/summary dialog.

/**
 * @registry Latitude/longitude coordinate pair.
 */
export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

/**
 * @registry Earth radius in feet, used for great-circle distance calculations.
 */
export const EARTH_RADIUS_FEET = 20925646.325;

/**
 * Distance a device recording is expected to occur within before warning.
 * @registry Feet threshold beyond which a device recording location triggers a warning.
 */
export const GEO_WARNING_THRESHOLD_FEET = 1500;

/**
 * @registry Converts degrees to radians.
 */
export const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Haversine great-circle distance between two coordinates, in feet.
 * @registry Great-circle distance in feet between two lat/long coordinates.
 */
export const haversineDistanceFeet = (
  a: GeoCoordinates,
  b: GeoCoordinates
): number => {
  const dLat = toRadians(b.latitude - a.latitude);
  const dLon = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);

  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

  return EARTH_RADIUS_FEET * c;
};

/**
 * Human-readable ft/yd distance — yards once the distance clears 300ft.
 * @registry Formats a feet distance as human-readable ft or yd string.
 */
export const formatDistanceFeet = (feet: number): string => {
  if (feet >= 300) {
    return `${Math.round(feet / 3).toLocaleString()} yd`;
  }
  return `${Math.round(feet).toLocaleString()} ft`;
};
